// src/Components/Buyerschoice.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import AboutCompany from "../Components/productListcomponents/companyContent";
import BuildProHomeContent from "../Components/BuildProHomeContent";

import axiosInstance from "./axiosInstance";
import whatsappIcon from "../Components/Assets/Calling.png";
import phoneIcon from "../Components/Assets/EmailICon.png";
import { baseurl } from "../config/url";


/* -------- Helper: Slug -------- */
const slug = (s = "") =>
    s.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");

const Buyerschoice = () => {
    const [slidercategories, setSliderCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const navigate = useNavigate();


    /* ---------- Fetch Categories + Subcategories ---------- */
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data: categoriesData } = await axiosInstance.get(
                    `${baseurl}/api/category/get-all`
                );

                const categoriesWithSub = await Promise.all(
                    (categoriesData || []).map(async (category) => {
                        try {
                            const { data: subs } = await axiosInstance.get(
                                `${baseurl}/api/subcategory/category/${category._id}`
                            );
                            return { ...category, subcategories: subs || [] };
                        } catch {
                            return { ...category, subcategories: [] };
                        }
                    })
                );

                setSliderCategories(categoriesWithSub);
            } catch (err) {
                console.error("Category fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    /* ---------- Navigate to Products ---------- */
    const handleImageClick = useCallback(
        (subcategoryId, categoryname) => {
            navigate(`/products/${subcategoryId}`, { state: { categoryname } });
        },
        [navigate]
    );

    /* ---------- Scroll to Category ---------- */
    const scrollToCategory = (name) => {
        const element = document.getElementById(`cat-${slug(name)}`);
        if (element) element.scrollIntoView({ behavior: "smooth" });
    };

    /* ---------- Toggle Expand ---------- */
    const toggleExpand = (categoryId) => {
        setExpanded((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    };

    return (
        <div className="min-h-screen bg-[#EFF6FF] pb-16">
            {/* ---------------- HEADER ---------------- */}
            <header className="pt-10 px-4 sm:px-6 text-center">
                <p className="inline-flex items-center gap-2 rounded-full border border-[#DDEBFF] bg-white/70 px-4 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#C7DFFF]" />
                    BuildPro-OGCS • Categories
                </p>

                <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent">
                    Explore Our Material Categories
                </h1>

                <p className="mt-2 text-sm text-slate-600 max-w-xl mx-auto">
                    High-quality construction materials organised category-wise. Tap a
                    category to explore product ranges.
                </p>
            </header>

            {/* ---------------- CATEGORY SHORTCUTS (Alphabetical) ---------------- */}
            <section className="mt-8 px-3 sm:px-6">
                <div className="max-w-7xl mx-auto bg-white/80 border border-slate-200 rounded-2xl shadow-sm px-4 sm:px-6 py-4">

                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <div className="text-center sm:text-left">
                            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">
                                Browse by category
                            </p>
                            <h2 className="text-sm sm:text-base font-semibold text-slate-900">
                                Quickly jump to a product family
                            </h2>
                        </div>

                        {!loading && slidercategories.length > 0 && (
                            <p className="text-[11px] text-slate-500 text-center sm:text-right">
                                Showing {slidercategories.length} categories
                            </p>
                        )}
                    </div>

                    {/* Chip list */}
                    <div
                        className="
        flex gap-2 sm:gap-3 overflow-x-auto no-scrollbar 
        py-1 sm:py-2
        md:flex-wrap md:justify-center
      "
                    >
                        {loading
                            ? Array.from({ length: 8 }).map((_, i) => (
                                <span
                                    key={i}
                                    className="inline-flex items-center justify-center 
                         px-4 h-9 rounded-full 
                         bg-[#DDEBFF]/80 animate-pulse"
                                />
                            ))
                            : [...slidercategories]                                     // clone array
                                .sort((a, b) => a.name.localeCompare(b.name))           // 🔥 SORT ALPHABETICALLY
                                .map((c) => (
                                    <button
                                        key={c._id}
                                        type="button"
                                        onClick={() => scrollToCategory(c.name)}
                                        className="
                  inline-flex items-center gap-2 
                  px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium
                  border border-[#C7DFFF] 
                  bg-[#F4F7FF] text-slate-800 
                  hover:bg-white hover:shadow-md
                  transition-all duration-150 
                  whitespace-nowrap
                "
                                    >
                                        {/* Left Icon Badge */}
                                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#DDEBFF] text-[11px] font-semibold text-slate-700">
                                            {c.name?.[0]?.toUpperCase() || "C"}
                                        </span>

                                        <span>{c.name}</span>
                                    </button>
                                ))}
                    </div>
                </div>
            </section>





            {/* ---------------- MAIN CONTENT  py-10 px-3 sm:px-6 lg:px-8---------------- */}
            <main className="max-w-7xl mx-auto px-3 sm:px-6 py-8">
                {/* ------- Loading Skeleton ------- */}
                {loading && (
                    <div className="space-y-10">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <section
                                key={i}
                                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200"
                            >
                                <div className="h-5 w-48 bg-[#DDEBFF] rounded mb-4 mx-auto" />
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                    {Array.from({ length: 4 }).map((__, j) => (
                                        <div
                                            key={j}
                                            className="rounded-xl bg-[#DDEBFF] h-[180px] animate-pulse"
                                        />
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                )}

                {/* ------- CATEGORY SECTIONS ------- */}
                {!loading &&
                    slidercategories.map((category, idx) => {
                        const subs = category.subcategories || [];
                        const isExpanded = expanded[category._id];
                        const visibleSubs = isExpanded ? subs : subs.slice(0, 4);
                        const hasMore = subs.length > 4;

                        return (
                            <section
                                key={category._id || idx}
                                id={`cat-${slug(category.name)}`}
                                className="mb-12"
                            >
                                {/* Category Title */}
                                <div className="mb-4 text-center">
                                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
                                        {category.name}
                                    </h2>
                                    {subs.length > 0 && (
                                        <p className="mt-1 text-xs text-slate-500">
                                            {subs.length} subcategories
                                        </p>
                                    )}
                                </div>

                                {/* Card Container bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-5 */}
                                <div className=" p-4 sm:p-5">
                                    {subs.length === 0 ? (
                                        <p className="text-sm text-slate-500 text-center">
                                            No subcategories added yet.
                                        </p>
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-5">
                                                {visibleSubs.map((subcategory) => (
                                                    <article
                                                        key={subcategory._id}
                                                        onClick={() =>
                                                            handleImageClick(
                                                                subcategory._id,
                                                                subcategory.name || category.name
                                                            )
                                                        }
                                                        className="group bg-white w-full text-left rounded-xl overflow-hidden 
                                       shadow-sm border border-slate-200 hover:shadow-md transition 
                                       cursor-pointer"
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                e.preventDefault();
                                                                handleImageClick(
                                                                    subcategory._id,
                                                                    subcategory.name || category.name
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {/* Image */}
                                                        <div className="relative w-full pb-[66%]">
                                                            {subcategory.image ? (
                                                                <img
                                                                    src={`${baseurl}${subcategory.image}`}
                                                                    alt={subcategory.name}
                                                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                                                />
                                                            ) : (
                                                                <div className="absolute inset-0 bg-[#E3EEFF] flex items-center justify-center text-slate-400 text-xs">
                                                                    No Image
                                                                </div>
                                                            )}
                                                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                                                                <p className="text-white font-semibold text-xs sm:text-sm truncate">
                                                                    {subcategory.name}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* Text */}
                                                        <div className="p-3">
                                                            <p className="text-[11px] text-slate-600 min-h-[32px] line-clamp-2">
                                                                {subcategory.description ||
                                                                    "Tap to view products in this range."}
                                                            </p>

                                                            {/* This is the ONLY button now */}
                                                            <button
                                                                type="button"
                                                                className="mt-2 w-full rounded-full px-3 py-1.5 text-[11px] font-medium 
                                           text-slate-700 bg-[#DDEBFF] hover:bg-[#C7DFFF] transition shadow-sm"
                                                            >
                                                                View Products
                                                            </button>
                                                        </div>
                                                    </article>
                                                ))}
                                            </div>

                                            {/* See More Button */}
                                            {hasMore && (
                                                <div className="flex justify-center mt-4">
                                                    <button
                                                        onClick={() => toggleExpand(category._id)}
                                                        className="px-4 py-1.5 text-xs font-medium rounded-full 
                                       border border-slate-300 bg-white text-slate-700 
                                       hover:bg-[#E3EEFF] shadow-sm transition"
                                                    >
                                                        {isExpanded
                                                            ? "See less"
                                                            : `See more (${subs.length - 4} more)`}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </section>
                        );
                    })}

                {/* Extra Sections */}
                <div className="mt-6">
                    <BuildProHomeContent />
                </div>

                <div className="mt-12">
                    <AboutCompany />
                </div>
            </main>

            {/* Floating Contact Buttons */}
            <div className="fixed bottom-5 right-5 flex flex-col gap-3 z-50">
                <a
                    href="https://wa.me/919967610135"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/90 backdrop-blur p-2 rounded-full shadow-lg ring-1 ring-slate-200 hover:scale-105 transition"
                >
                    <img src={whatsappIcon} alt="WhatsApp" className="w-11 h-11" />
                </a>

                <a
                    href="mailto:ogcspvtlt@gmail.com"
                    className="bg-white/90 backdrop-blur p-2 rounded-full shadow-lg ring-1 ring-slate-200 hover:scale-105 transition"
                >
                    <img src={phoneIcon} alt="Email" className="w-11 h-11" />
                </a>
            </div>
        </div>
    );
};

export default Buyerschoice;
